import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CustomSelect, { type SelectOption } from '@/components/ui/CustomSelect';
import { TOURNAMENT_ARCHIVE_FIRST_SEASON, TOURNAMENT_ROLES } from '@/constants';
import type { TournamentRole } from '@/constants';
import styles from './styles.module.scss';

export interface TournamentSeasonRoleFiltersProps {
  season: string | null;
  onSeasonChange: (value: string | null) => void;
  role: TournamentRole | null;
  onRoleChange: (value: TournamentRole | null) => void;
  /** Builds element ids and `data-testid`s: `${idPrefix}-season`, `${idPrefix}-role`. */
  idPrefix: string;
}

/** Select value for "no filter". */
const ALL = 'all';

/** Seasons from `TOURNAMENT_ARCHIVE_FIRST_SEASON` to the current year, newest first. */
function seasonYears(): string[] {
  const years: string[] = [];
  for (let year = new Date().getFullYear(); year >= TOURNAMENT_ARCHIVE_FIRST_SEASON; year -= 1) {
    years.push(String(year));
  }
  return years;
}

/**
 * Year and role dropdowns (archive list). Two equal halves on phones, content
 * width next to the search from tablet up.
 */
export const TournamentSeasonRoleFilters: React.FC<TournamentSeasonRoleFiltersProps> = ({
  season,
  onSeasonChange,
  role,
  onRoleChange,
  idPrefix,
}) => {
  const { t } = useTranslation('tournament');

  const seasonOptions = useMemo<SelectOption[]>(() => [
    { value: ALL, label: t('tournament:list.allYears') },
    ...seasonYears().map((year) => ({ value: year, label: year })),
  ], [t]);

  const roleOptions = useMemo<SelectOption[]>(() => [
    { value: ALL, label: t('tournament:allRoles') },
    { value: TOURNAMENT_ROLES.HOLDER, label: t('tournament:list.role.holder') },
    { value: TOURNAMENT_ROLES.MAINTAINER, label: t('tournament:list.role.maintainer') },
    { value: TOURNAMENT_ROLES.PARTICIPANT, label: t('tournament:list.role.participant') },
  ], [t]);

  return (
    <div className={styles.filters}>
      <div className={styles.select} data-testid={`${idPrefix}-season`}>
        <CustomSelect
          id={`${idPrefix}-season`}
          ariaLabel={t('tournament:list.yearFilterLabel')}
          options={seasonOptions}
          value={seasonOptions.find((option) => option.value === (season ?? ALL)) ?? seasonOptions[0]}
          onChange={(option) => onSeasonChange(option && option.value !== ALL ? option.value : null)}
          variant="basic"
          isSearchable={false}
          menuPortal
        />
      </div>
      <div className={styles.select} data-testid={`${idPrefix}-role`}>
        <CustomSelect
          id={`${idPrefix}-role`}
          ariaLabel={t('tournament:role')}
          options={roleOptions}
          value={roleOptions.find((option) => option.value === (role ?? ALL)) ?? roleOptions[0]}
          onChange={(option) => onRoleChange(
            option && option.value !== ALL ? (option.value as TournamentRole) : null,
          )}
          variant="basic"
          isSearchable={false}
          menuPortal
        />
      </div>
    </div>
  );
};

export default TournamentSeasonRoleFilters;
